import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Heart, ShoppingBag, Truck, Shield, Award } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { ImageCarousel, productImages } from "../components/ImageCarousel";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!p) return <div className="min-h-screen pt-40 text-center text-[var(--twr-warm-white)]/55">Loading…</div>;

  return (
    <section className="pt-32 pb-24 px-6 sm:px-10 lg:px-16" data-testid="product-detail-page">
      <div className="max-w-[1500px] mx-auto">
        <Link to="/shop" className="inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/60 hover:text-[var(--twr-bronze)] transition-colors">
          <ChevronLeft size={14} /> Back to Shop
        </Link>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mt-10">
          <div className="frame aspect-square">
            <ImageCarousel images={productImages(p)} alt={p.name} testId="product-detail-image" />
          </div>
          <div>
            <p className="eyebrow">{p.category.replace(/-/g, " ")}{p.origin ? ` · ${p.origin}` : ""}</p>
            <h1 className="font-serif text-4xl sm:text-5xl mt-5 leading-tight" data-testid="product-detail-name">{p.name}</h1>
            <p className="font-serif text-3xl text-[var(--twr-bronze)] mt-5" data-testid="product-detail-price">${p.price.toFixed(2)}</p>
            <div className="hairline my-8" />
            <p className="text-[var(--twr-warm-white)]/80 leading-relaxed">{p.long_description || p.description}</p>

            {(p.strength || p.origin) && (
              <div className="grid grid-cols-2 gap-4 mt-10">
                {p.origin && <Spec label="Origin" value={p.origin} />}
                {p.strength && <Spec label="Strength" value={p.strength} />}
              </div>
            )}

            <div className="flex items-center gap-3 mt-10">
              <div className="inline-flex items-center border border-[var(--twr-line-strong)]">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="product-qty-dec" className="px-4 py-3 hover:text-[var(--twr-bronze)] transition-colors">−</button>
                <span className="px-5 py-3 font-serif text-lg" data-testid="product-qty">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} data-testid="product-qty-inc" className="px-4 py-3 hover:text-[var(--twr-bronze)] transition-colors">+</button>
              </div>
              <button
                data-testid="product-add-to-cart"
                onClick={() => { add(p, qty); toast.success(`Added ${qty} × ${p.name}`); }}
                className="btn-bronze flex-1"
              >
                <ShoppingBag size={14} /> Add to Cart
              </button>
              <button data-testid="product-wishlist" className="btn-ghost px-4">
                <Heart size={14} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-12">
              {[
                { Icon: Truck, t: "Complimentary Delivery", s: "On orders $250+" },
                { Icon: Shield, t: "Authenticity", s: "Direct from house" },
                { Icon: Award, t: "Concierge", s: "By appointment" },
              ].map(({ Icon, t, s }) => (
                <div key={t} className="surface p-5 text-center">
                  <Icon size={18} className="text-[var(--twr-bronze)] mx-auto" strokeWidth={1.4} />
                  <p className="text-[11px] tracking-[0.22em] uppercase mt-3">{t}</p>
                  <p className="text-[var(--twr-warm-white)]/55 text-[11px] mt-1">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Spec({ label, value }) {
  return (
    <div className="surface p-4">
      <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">{label}</p>
      <p className="font-serif text-lg mt-2">{value}</p>
    </div>
  );
}
